import {
  BorderStrokeStyleDefinition,
  HexCode,
  defaultBrandKit,
  defaultTertiaryButton,
} from "@redotech/redo-content-builder-common/brand-kit";
import {
  Alignment,
  ButtonAction,
  ButtonElement,
  ColumnElement,
  CustomFieldType,
  DiscountCodeElement,
  FontFamily,
  FontWeight,
  ImageElement,
  InputElement,
  MarketingForgeElementType,
  RichTextElement,
  TextToSubscribeButtonElement,
  TextToSubscribeDescriptionElement,
} from "@redotech/redo-content-builder-common/element-schema";
import { CustomerCustomFieldDataType } from "@redotech/redo-customer-segments-common/customer-custom-fields/customer-custom-fields-definition";
import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { SignupForm } from "./signup-form-schema";

export const imageDirections = [
  "top",
  "right",
  "bottom",
  "left",
  "no",
  "background",
] as const;
export type ImageDirection = (typeof imageDirections)[number];
export const ImageDirection = imageDirections;

export enum SignUpFormGoal {
  MARKETING_SUBSCRIPTION = "marketing-subscription",
  BACK_IN_STOCK = "back-in-stock",
}

export const SIGNUP_FORM_ANALYTICS_MIGRATION_DATE = new Date("2025-10-03");

export const prettySignUpFormGoal: Record<SignUpFormGoal, string> = {
  [SignUpFormGoal.MARKETING_SUBSCRIPTION]: "Marketing subscription",
  [SignUpFormGoal.BACK_IN_STOCK]: "Back in stock",
};

export enum LayoutType {
  POPUP = "popup",
  FULLSCREEN = "fullscreen",
  EMBEDDED = "embedded",
}

export const customFieldTypeToDataType: Record<
  CustomFieldType,
  CustomerCustomFieldDataType
> = {
  [CustomFieldType.Date]: CustomerCustomFieldDataType.DATE,
  [CustomFieldType.Dropdown]: CustomerCustomFieldDataType.STRING,
  [CustomFieldType.Number]: CustomerCustomFieldDataType.NUMBER,
  [CustomFieldType.TextLong]: CustomerCustomFieldDataType.STRING,
  [CustomFieldType.TextShort]: CustomerCustomFieldDataType.STRING,
  [CustomFieldType.YesNoQuestion]: CustomerCustomFieldDataType.BOOLEAN,
};

export const captureGoalTypes = ["phone", "email", "both"] as const;
export type CaptureGoalType = (typeof captureGoalTypes)[number];

export const deviceOptions = ["desktop", "mobile", "desktop & mobile"] as const;
export type DeviceOption = (typeof deviceOptions)[number];
export const DeviceOption = deviceOptions;

export const signupFormSiteTypes = [
  "tracking-page",
  "marketing",
  "customer-account",
] as const;
export type SignupFormSiteType = (typeof signupFormSiteTypes)[number];
export const SignupFormSiteType = signupFormSiteTypes;

export const discountTypes = ["percent", "fixed"] as const;
export type DiscountType = (typeof discountTypes)[number];

export const audienceTypes = ["all visitors", "based on campaign"] as const;
export type AudienceType = (typeof audienceTypes)[number];
export const AudienceType = audienceTypes;

export const targetingTypes = [
  "all visitors",
  "exclude existing contacts",
  "only existing contacts",
] as const;
export type TargetingType = (typeof targetingTypes)[number];
export const TargetingType = targetingTypes;

export const teaserVisibility = ["before-displaying", "after-closing"] as const;
export type TeaserVisibility = (typeof teaserVisibility)[number];
export const TeaserVisibility = teaserVisibility;

export const alignmentHorizontalValues = ["left", "center", "right"] as const;
export type AlignmentHorizontal = (typeof alignmentHorizontalValues)[number];
export const AlignmentHorizontal = alignmentHorizontalValues;

export const alignmentVerticalValues = ["top", "center", "bottom"] as const;
export type AlignmentVertical = (typeof alignmentVerticalValues)[number];
export const AlignmentVertical = alignmentVerticalValues;

export interface Alignment2D {
  vertical: AlignmentVertical;
  horizontal: AlignmentHorizontal;
}

export enum URLVisibilityType {
  INCLUSIVE = "inclusive",
  EXCLUSIVE = "exclusive",
}

export enum URLMatchingType {
  EXACT = "exact",
  CONTAINS = "contains",
}

export const fontWeightValues: Record<FontWeight, number> = {
  regular: 400,
  semibold: 600,
  bold: 700,
};

export interface FormStep {
  elements: FormElement[];
}

export const signupFormSplitDBSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  active: z.boolean(),
  startDate: z.date(),
  deviceTarget: createEnum(deviceOptions, { enumName: "DeviceOption" }),
  treatments: z.array(zExt.objectId()),
  deleted: z.boolean(),
});

export type SignupFormSplitDBSchema = z.infer<typeof signupFormSplitDBSchema>;

export const signupFormSplitTreatment = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  formId: zExt.objectId(),
  weight: z.number(),
});
export type SignupFormSplitTreatment = z.infer<typeof signupFormSplitTreatment>;

export const signupFormSplitTreatmentWithMetrics =
  signupFormSplitTreatment.extend({
    views: z.number(),
    emailSignups: z.number(),
    smsSignups: z.number(),
    conversionRate: z.number(),
  });
export type SignupFormSplitTreatmentWithMetrics = z.infer<
  typeof signupFormSplitTreatmentWithMetrics
>;

const signupFormSplitBase = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  active: z.boolean(),
  startDate: z.date(),
  deviceTarget: createEnum(deviceOptions, { enumName: "DeviceOption" }),
});

export const signupFormSplit = signupFormSplitBase.extend({
  treatments: z.array(signupFormSplitTreatment),
});
export type SignupFormSplit = z.infer<typeof signupFormSplit>;

export const signupFormSplitWithMetrics = signupFormSplitBase.extend({
  treatments: z.array(signupFormSplitTreatmentWithMetrics),
});
export type SignupFormSplitWithMetrics = z.infer<
  typeof signupFormSplitWithMetrics
>;

export const variantSchema = z.object({
  formId: z.string(),
  weight: z.number().min(0).max(100),
  name: z.string().min(1, "Variant name is required"),
});

export const createAbTestFormParser = z.object({
  name: z.string().min(1, "Test name is required"),
  variants: z
    .array(variantSchema)
    .min(2, "At least 2 variants required")
    .max(3, "Maximum 3 variants allowed per test")
    .refine(
      (variants) => {
        const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);
        return totalWeight === 100;
      },
      { message: "Variant weights must sum to 100%" },
    ),
});
export interface Teaser {
  text: string;
  fontFamily: string;
  fontWeight: FontWeight;
  fontSizePx: number;
  positionHorizontal: AlignmentHorizontal;
  positionVertical: AlignmentVertical;
  backgroundColor: HexCode;
  textColor: HexCode;
  cornerRadiusPx: number;
  borderStroke: BorderStrokeStyleDefinition;
  iconVisible: boolean;
  visibility: TeaserVisibility;
}

export type FormElement =
  | RichTextElement
  | ButtonElement
  | InputElement
  | ImageElement
  | ColumnElement
  | DiscountCodeElement
  | TextToSubscribeButtonElement
  | TextToSubscribeDescriptionElement;

const surveyStep: FormStep = {
  elements: [
    {
      id: 1,
      type: MarketingForgeElementType.RICH_TEXT,
      html: "<p><b>Get 10% off</b></p>",
      fontFamily: FontFamily.ARIAL,
      fontWeight: "bold",
      fontSizePx: 32,
      textColor: "#000000",
      linkColor: "#0000ff",
    },
    {
      id: 2,
      type: MarketingForgeElementType.RICH_TEXT,
      html: "<p>Join our mailing list today and get a discount on your next order. Interested?</p>",
      fontFamily: FontFamily.ARIAL,
      fontWeight: "regular",
      fontSizePx: 16,
      textColor: "#000000",
      linkColor: "#0000ff",
    },
    {
      type: MarketingForgeElementType.BUTTON,
      hierarchy: "primary",
      text: "Yes, sign me up",
      action: ButtonAction.SUBMIT_AND_ADVANCE,
    },
    {
      type: MarketingForgeElementType.BUTTON,
      hierarchy: "secondary",
      text: "No thanks",
      action: ButtonAction.CLOSE,
    },
  ],
};

export const marketingCaptureStep = (
  captureGoal: CaptureGoalType,
  isEmbedded = false,
): FormStep => {
  const step: FormStep = {
    elements: [
      {
        id: 1,
        type: MarketingForgeElementType.RICH_TEXT,
        html: isEmbedded
          ? '<p style="text-align: center;">Sign up for our VIP newsletter</p>'
          : "<p><b>Get 10% off your first order</b></p>",
        fontFamily: FontFamily.ARIAL,
        fontWeight: isEmbedded ? "regular" : "bold",
        fontSizePx: 32,
        textColor: "#000000",
        linkColor: "#0000ff",
      },
      {
        id: 2,
        type: MarketingForgeElementType.RICH_TEXT,
        html: isEmbedded
          ? '<p style="text-align: center;">You\'ll be the first to know about exclusive products and offers.</p>'
          : "<p>Sign up for special offers and updates</p>",
        fontFamily: FontFamily.ARIAL,
        fontWeight: "regular",
        fontSizePx: 16,
        textColor: "#000000",
        linkColor: "#0000ff",
      },
    ],
  };

  if (captureGoal === "email" || captureGoal === "both") {
    step.elements.push({
      type: MarketingForgeElementType.INPUT,
      inputType: "email",
      placeholderText: "Enter your email address",
      optional: false,
    });
  }

  if (captureGoal === "phone" || captureGoal === "both") {
    step.elements.push({
      type: MarketingForgeElementType.INPUT,
      inputType: "phone",
      placeholderText: "Enter your phone number",
      optional: false,
    });
  }

  step.elements.push({
    type: MarketingForgeElementType.BUTTON,
    hierarchy: "primary",
    text: isEmbedded ? "Submit" : "Sign up",
    action: ButtonAction.SUBMIT_AND_ADVANCE,
  });

  return step;
};

const successStep = (isEmbedded: boolean): FormStep => {
  const elements: FormElement[] = [
    {
      id: 1,
      type: MarketingForgeElementType.RICH_TEXT,
      html: '<p style="text-align: center;">Thank you for subscribing!</p>',
      fontFamily: FontFamily.ARIAL,
      fontWeight: "bold",
      fontSizePx: 32,
      textColor: "#000000",
      linkColor: "#0000ff",
    },
  ];

  if (isEmbedded) {
    elements.push({
      type: MarketingForgeElementType.BUTTON,
      hierarchy: "primary",
      text: "Close",
      action: ButtonAction.CLOSE,
    });
  }

  return { elements };
};

export const backInStockCaptureStep = (
  captureGoal: CaptureGoalType,
): FormStep => {
  const step: FormStep = {
    elements: [
      {
        id: 1,
        type: MarketingForgeElementType.RICH_TEXT,
        html: '<p style="text-align: center;">Get notified</p>',
        fontFamily: FontFamily.ARIAL,
        fontWeight: "bold",
        fontSizePx: 32,
        textColor: "#000000",
        linkColor: "#0000ff",
      },
      {
        id: 2,
        type: MarketingForgeElementType.RICH_TEXT,
        html: '<p style="text-align: center;">We will let you know if this item is restocked.</p>',
        fontFamily: FontFamily.ARIAL,
        fontWeight: "regular",
        fontSizePx: 16,
        textColor: "#000000",
        linkColor: "#0000ff",
      },
    ],
  };

  if (captureGoal === "email" || captureGoal === "both") {
    step.elements.push({
      type: MarketingForgeElementType.INPUT,
      inputType: "email",
      placeholderText: "Enter your email address",
      optional: false,
    });
  }

  step.elements.push({
    type: MarketingForgeElementType.BUTTON,
    hierarchy: "primary",
    text: "Submit",
    action: ButtonAction.SUBMIT_AND_ADVANCE,
  });

  return step;
};

// Type for elements that can be nested inside columns (excludes ColumnElement to prevent recursion)
export type NestedFormElement = Exclude<FormElement, ColumnElement>;

export const SUBSCRIBE_MESSAGE =
  "Send this text to subscribe to recurring automated personalized marketing alerts (e.g. cart reminders)";
export const SUBSCRIBE_DESCRIPTION =
  "By clicking this button, you agree to receive recurring automated marketing text messages (e.g. AI content, cart reminders) from this merchant at the number you provide. Consent not a condition of purchase. We may share info with service providers per our Privacy Policy. Reply HELP for help & STOP to cancel. Msg frequency varies. Msg & data rates may apply. By clicking this button, you also agree to our Terms (incl. arbitration), Privacy Policy & Giveaway Terms.";

/**
 * Creates default form elements for the form builder
 */
export const createDefaultFormElement = (
  elementType: MarketingForgeElementType,
  nextId?: number,
): FormElement => {
  switch (elementType) {
    case MarketingForgeElementType.INPUT:
      return {
        type: MarketingForgeElementType.INPUT,
        inputType: "email",
        placeholderText: "Email address",
        optional: false,
      };
    case MarketingForgeElementType.RICH_TEXT:
      return {
        id: nextId ?? 1,
        type: MarketingForgeElementType.RICH_TEXT,
        html: '<p style="text-align: center;"></p>',
        fontFamily: FontFamily.ARIAL,
        fontWeight: "bold",
        fontSizePx: 24,
        textColor: "#000000",
        linkColor: "#0000ff",
      };
    case MarketingForgeElementType.BUTTON:
      return {
        type: MarketingForgeElementType.BUTTON,
        hierarchy: "primary",
        text: "Sign up",
        action: ButtonAction.SUBMIT_AND_ADVANCE,
      };
    case MarketingForgeElementType.IMAGE:
      return { type: MarketingForgeElementType.IMAGE, url: "" };
    case MarketingForgeElementType.PRODUCT:
      throw new Error("Product element not supported in signup form");
    case MarketingForgeElementType.COLUMN:
      return {
        type: MarketingForgeElementType.COLUMN,
        elements: [],
        totalColumns: 1,
      };
    case MarketingForgeElementType.DISCOUNT:
      return {
        type: MarketingForgeElementType.DISCOUNT,
        fontFamily: FontFamily.ARIAL,
        fontWeight: "bold",
        fontSize: 20,
        textColor: "#000000",
        blockBackgroundColor: "#ffffff",
        sectionColor: "#ffffff",
        sectionPadding: { top: 0, right: 0, bottom: 0, left: 0 },
        alignment: Alignment.CENTER,
        borderRadius: 16,
      };
    case MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON:
      return {
        type: MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON,
        text: "Subscribe to SMS",
        hierarchy: "primary",
        action: ButtonAction.SUBMIT_AND_ADVANCE,
      };
    case MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION:
      return {
        type: MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION,
        fontFamily: FontFamily.ARIAL,
        textColor: "#000000",
      };
    default:
      throw new Error(`Unknown element type: ${elementType}`);
  }
};

/**
 * Creates default form elements that can be nested inside columns
 */
export function createDefaultNestedElement<
  T extends Exclude<
    MarketingForgeElementType,
    MarketingForgeElementType.COLUMN
  >,
>(elementType: T, nextId?: number): NestedFormElement {
  return createDefaultFormElement(elementType, nextId) as NestedFormElement;
}

export const getExampleSteps = (
  goal: SignUpFormGoal,
  captureGoal: CaptureGoalType,
  includeSurvey: boolean,
  type: LayoutType,
) => {
  const steps: FormStep[] = [];

  switch (goal) {
    case SignUpFormGoal.BACK_IN_STOCK: {
      steps.push(backInStockCaptureStep(captureGoal));
      steps.push(successStep(type === LayoutType.EMBEDDED));
      return steps;
    }
    case SignUpFormGoal.MARKETING_SUBSCRIPTION: {
      if (includeSurvey && type !== LayoutType.EMBEDDED) {
        steps.push(surveyStep);
      }

      const isEmbedded = type === LayoutType.EMBEDDED;
      steps.push(marketingCaptureStep(captureGoal, isEmbedded));
      steps.push(successStep(isEmbedded));
      return steps;
    }
    default:
      assertNever(goal);
  }
};

export const getExampleForm = ({
  name,
  goal,
  captureGoal,
  includeSurvey,
  type,
  includeTeaser,
}: {
  name: string;
  goal: SignUpFormGoal;
  captureGoal: CaptureGoalType;
  includeSurvey: boolean;
  type: LayoutType;
  includeTeaser: boolean;
}): SignupForm => {
  return {
    name,
    goal,
    theme: {
      overrides: {
        buttons: {
          enabled: false,
          primary: {
            ...defaultBrandKit?.buttons.primary,
            font: {
              fontSizePx: defaultBrandKit?.font.hierarchy.body.fontSizePx,
              fontWeight: defaultBrandKit?.font.hierarchy.body.fontWeight,
              fontFamily: defaultBrandKit?.font.bodyFontFamily,
            },
          },
          secondary: {
            ...defaultBrandKit?.buttons.secondary,
            font: {
              fontSizePx: defaultBrandKit?.font.hierarchy.body.fontSizePx,
              fontWeight: defaultBrandKit?.font.hierarchy.body.fontWeight,
              fontFamily: defaultBrandKit?.font.bodyFontFamily,
            },
          },
          tertiary: {
            ...defaultTertiaryButton,
            font: {
              fontSizePx: defaultBrandKit?.font.hierarchy.body.fontSizePx,
              fontWeight: defaultBrandKit?.font.hierarchy.body.fontWeight,
              fontFamily: defaultBrandKit?.font.bodyFontFamily,
            },
          },
        },
        inputs: {
          ...defaultBrandKit?.inputs,
          enabled: false,
          font: {
            fontSizePx: defaultBrandKit?.font.hierarchy.body.fontSizePx,
            fontWeight: defaultBrandKit?.font.hierarchy.body.fontWeight,
            fontFamily: defaultBrandKit?.font.bodyFontFamily,
          },
        },
      },
      closeButton: {
        color: "#000000",
        background: { enabled: false, color: "#ffffff" },
      },
      border: { cornerRadiusPx: 0, stroke: { color: "#000000", weightPx: 0 } },
      overlay: {
        color: "#000000",
        opacityPercent: 75,
        popupOpacityPercent: 100,
      },
    },
    layout: {
      type,
      backgroundColor: "#ffffff",
      image: {
        style:
          type === LayoutType.EMBEDDED || goal === SignUpFormGoal.BACK_IN_STOCK
            ? "no"
            : "left",
        url: "",
        hideOnMobile: false,
      },
    },
    discount: { enabled: false, discountId: undefined },
    behavior: {
      appearsOn: "desktop & mobile",
      appearsOnSite: "marketing",
      subscriberTags: [`${name.toLowerCase().split(" ").join("_")}_redo_popup`],
      display: {
        timeOnPage: { enabled: true, seconds: 10 },
        exitIntent: { enabled: false },
      },
      audience: "all visitors",
      campaignUtms: [],
      targeting: "all visitors",
      embeddedCode: getEmbeddedCode(),
      urlVisibility: {
        type: URLVisibilityType.EXCLUSIVE,
        urls: [],
        matchingType: URLMatchingType.EXACT,
      },
    },
    teaser: {
      enabled: includeTeaser,
      text: "Get 10% off",
      fontFamily: FontFamily.ARIAL,
      fontWeight: "bold",
      fontSizePx: 16,
      positionHorizontal: "left",
      positionVertical: "bottom",
      backgroundColor: "#000000",
      textColor: "#ffffff",
      cornerRadiusPx: 5,
      borderStroke: { color: "#000000", weightPx: 0 },
      iconVisible: false,
      visibility: "before-displaying",
    },
    pdpButton:
      goal === SignUpFormGoal.BACK_IN_STOCK
        ? {
            hierarchy: "primary",
            text: "Notify me when available",
            action: ButtonAction.SUBMIT_AND_ADVANCE,
            type: MarketingForgeElementType.BUTTON,
          }
        : undefined,
    steps: getExampleSteps(goal, captureGoal, includeSurvey, type),
  };
};

export const stepHasDiscountElement = (step: FormStep): boolean => {
  if (!step.elements) {
    return false;
  }
  return step.elements.some((element) => {
    if (element.type === MarketingForgeElementType.DISCOUNT) {
      return true;
    }
    if (element.type === MarketingForgeElementType.COLUMN && element.elements) {
      return element.elements.some((colElement) => {
        if (colElement.type === MarketingForgeElementType.DISCOUNT) {
          return true;
        }
        return false;
      });
    }
    return false;
  });
};

export const getEmbeddedCode = () => {
  const randomFiveLetterString = Math.random().toString(36).substring(2, 7);
  return `<div id="redo-marketing-embedded-form-${randomFiveLetterString}"></div>`;
};
